import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/projects/IPO/components/ui/button";
import { Badge } from "@/projects/IPO/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/projects/IPO/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/projects/IPO/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/projects/IPO/components/ui/table";
// import IPOService from "@/services/IPO/APIs/IPOService"; // Commented out - using dummy data
import { IPO } from "@/utils/types/IPO/ipo";
import {
  TrendingUp,
  AlertCircle,
  Users,
  ChevronRight,
  Target,
  Calendar,
  ArrowUpRight,
  Briefcase,
  Star,
  Sparkles,
  BarChart3,
} from "lucide-react";
import ProductLayout from "@/projects/IPO/components/ProductLayout";
import { ProviderIcon } from "@/projects/IPO/components/ProviderIcon";
import { motion } from "framer-motion";

// Dummy IPO data
const getDummyIPOs = (): IPO[] => {
  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  const nextMonth = new Date(today);
  nextMonth.setDate(today.getDate() + 30);
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  return [
    {
      id: "1",
      companyName: "TechCorp Solutions Limited",
      companyShortName: "TechCorp",
      type: "Main Board",
      status: "Open",
      iconType: "tech",
      priceRange: { min: 120, max: 125 },
      lotSize: 120,
      minInvestment: 15000,
      issueSize: "₹1,200 Cr",
      bidDates: {
        start: today.toISOString().split("T")[0],
        end: nextWeek.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextWeek.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextWeek.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 2.45,
        nii: 1.89,
        retail: 3.12,
        total: 2.68,
      },
      gmp: 15,
      aboutCompany: "Leading technology solutions provider",
    },
    {
      id: "2",
      companyName: "GreenEnergy Power Limited",
      companyShortName: "GreenEnergy",
      type: "Main Board",
      status: "Open",
      iconType: "energy",
      priceRange: { min: 85, max: 90 },
      lotSize: 200,
      minInvestment: 18000,
      issueSize: "₹850 Cr",
      bidDates: {
        start: today.toISOString().split("T")[0],
        end: nextWeek.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextWeek.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextWeek.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 1.56,
        nii: 0.98,
        retail: 2.34,
        total: 1.89,
      },
      gmp: 8,
      aboutCompany: "Renewable energy solutions",
    },
    {
      id: "3",
      companyName: "MediCare Pharmaceuticals Limited",
      companyShortName: "MediCare",
      type: "Main Board",
      status: "Upcoming",
      iconType: "pharma",
      priceRange: { min: 250, max: 260 },
      lotSize: 60,
      minInvestment: 15600,
      issueSize: "₹2,500 Cr",
      bidDates: {
        start: nextWeek.toISOString().split("T")[0],
        end: nextMonth.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextMonth.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextMonth.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextMonth.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 0,
        nii: 0,
        retail: 0,
        total: 0,
      },
      gmp: 25,
      aboutCompany: "Pharmaceutical manufacturing",
    },
    {
      id: "4",
      companyName: "FinTech Innovations Private Limited",
      companyShortName: "FinTech",
      type: "SME",
      status: "Open",
      iconType: "fintech",
      priceRange: { min: 45, max: 50 },
      lotSize: 300,
      minInvestment: 15000,
      issueSize: "₹150 Cr",
      bidDates: {
        start: today.toISOString().split("T")[0],
        end: nextWeek.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextWeek.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextWeek.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 0.45,
        nii: 0.32,
        retail: 0.78,
        total: 0.56,
      },
      gmp: 5,
      aboutCompany: "Financial technology solutions",
    },
    {
      id: "5",
      companyName: "AgriGrow Limited",
      companyShortName: "AgriGrow",
      type: "SME",
      status: "Upcoming",
      iconType: "agriculture",
      priceRange: { min: 30, max: 35 },
      lotSize: 400,
      minInvestment: 14000,
      issueSize: "₹120 Cr",
      bidDates: {
        start: nextWeek.toISOString().split("T")[0],
        end: nextMonth.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextMonth.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextMonth.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextMonth.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 0,
        nii: 0,
        retail: 0,
        total: 0,
      },
      gmp: 3,
      aboutCompany: "Agricultural products and services",
    },
    {
      id: "6",
      companyName: "RetailMart Limited",
      companyShortName: "RetailMart",
      type: "Main Board",
      status: "Closed",
      iconType: "retail",
      priceRange: { min: 180, max: 190 },
      lotSize: 100,
      minInvestment: 19000,
      issueSize: "₹1,800 Cr",
      bidDates: {
        start: lastWeek.toISOString().split("T")[0],
        end: today.toISOString().split("T")[0],
      },
      allotmentDate: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 4.56,
        nii: 3.21,
        retail: 5.89,
        total: 4.78,
      },
      gmp: 20,
      aboutCompany: "Retail chain operations",
    },
    {
      id: "7",
      companyName: "EduTech Solutions Limited",
      companyShortName: "EduTech",
      type: "Main Board",
      status: "Open",
      iconType: "education",
      priceRange: { min: 95, max: 100 },
      lotSize: 150,
      minInvestment: 15000,
      issueSize: "₹950 Cr",
      bidDates: {
        start: today.toISOString().split("T")[0],
        end: nextWeek.toISOString().split("T")[0],
      },
      allotmentDate: new Date(nextWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(nextWeek.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(nextWeek.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 1.23,
        nii: 0.89,
        retail: 1.67,
        total: 1.34,
      },
      gmp: 12,
      aboutCompany: "Educational technology platform",
    },
    {
      id: "8",
      companyName: "LogiChain Services Limited",
      companyShortName: "LogiChain",
      type: "SME",
      status: "Closed",
      iconType: "logistics",
      priceRange: { min: 55, max: 60 },
      lotSize: 250,
      minInvestment: 15000,
      issueSize: "₹200 Cr",
      bidDates: {
        start: lastWeek.toISOString().split("T")[0],
        end: today.toISOString().split("T")[0],
      },
      allotmentDate: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      refundDate: new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      listingDate: new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      subscriptionRate: {
        qib: 2.34,
        nii: 1.56,
        retail: 2.89,
        total: 2.45,
      },
      gmp: 10,
      aboutCompany: "Logistics and supply chain services",
    },
  ];
};

const IPOHomePage = () => {
  const [ipos, setIpos] = useState<IPO[]>(getDummyIPOs());
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Open");
  const [boardFilter, setBoardFilter] = useState("all");

  // Using dummy data instead of API call
  useEffect(() => {
    // Uncomment below to use API instead of dummy data
    /*
    const fetchIpos = async () => {
      try {
        console.log("Fetching IPOs from API...");
        const response = await IPOService.list();
        console.log("API Response:", response);
        console.log("Response data:", response.data);

        if (!response.data || !Array.isArray(response.data)) {
          console.error("Invalid response format:", response);
          setIpos([]);
          return;
        }

        // Map backend fields to frontend IPO interface
        const mappedData = response.data.map((item: any) => ({
          id: item.id,
          companyName: item.company_name,
          companyShortName: item.company_short_name,
          type: item.ipo_type,
          status: item.status,
          iconType: item.icon_type,
          priceRange: { min: item.price_min, max: item.price_max },
          lotSize: item.lot_size,
          minInvestment: item.min_investment,
          issueSize: item.issue_size,
          bidDates: { start: item.bid_start_date, end: item.bid_end_date },
          allotmentDate: item.allotment_date,
          refundDate: item.refund_date,
          listingDate: item.listing_date,
          subscriptionRate: {
            qib: item.subscription_qib,
            nii: item.subscription_nii,
            retail: item.subscription_retail,
            total: item.subscription_total,
          },
          gmp: item.gmp,
          aboutCompany: item.about_company,
        }));
        console.log("Mapped IPO data:", mappedData);
        setIpos(mappedData);
      } catch (error) {
        console.error("Error fetching IPOs:", error);
        console.error(
          "Error details:",
          error instanceof Error ? error.message : error
        );
        setIpos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIpos();
    */

    // Using dummy data
    setIpos(getDummyIPOs());
    setLoading(false);
  }, []);

  const filteredIPOs = ipos.filter(ipo => {
    const matchesStatus = ipo.status === statusFilter;
    const matchesBoard = boardFilter === "all" || ipo.type === boardFilter;
    return matchesStatus && matchesBoard;
  });

  const openIPOs = ipos.filter(ipo => ipo.status === "Open").length;
  const upcomingIPOs = ipos.filter(ipo => ipo.status === "Upcoming").length;

  // Countdown timer for open IPOs (can be used for future enhancements)
  useEffect(() => {
    const timer = setInterval(() => {
      // Countdown logic can be implemented here if needed
      // Currently kept for future use
    }, 1000);

    return () => clearInterval(timer);
  }, [ipos]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-IN", { month: "short" });
    return { day, month };
  };

  if (loading) {
    return (
      <ProductLayout>
        <div className="container mx-auto px-4 py-10 md:px-6">
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">Loading IPOs...</p>
          </div>
        </div>
      </ProductLayout>
    );
  }

  return (
    <ProductLayout>
      {/* Full-Width immersive Hero Section - Compact & Top-aligned */}
      <div className="group relative -mt-20 w-full overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#0c4a6e] via-[#075985] to-[#0369a1]">
        {/* Decorative Elements */}
        <div className="pointer-events-none absolute right-0 top-0 -mr-40 -mt-40 h-[600px] w-[600px] rounded-full bg-[#1dab91] opacity-[0.06] blur-[150px]" />
        <div className="pointer-events-none absolute bottom-0 left-0 -mb-20 -ml-20 h-[400px] w-[400px] rounded-full bg-sky-400 opacity-[0.06] blur-[120px]" />

        <div className="container relative z-10 mx-auto grid items-center gap-8 px-4 pb-12 pt-32 md:px-6 md:pb-20 lg:grid-cols-2">
          {/* Left Side: Copy and CTAs */}
          <div className="space-y-6 delay-100 duration-700 animate-in fade-in slide-in-from-left-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 shadow-inner backdrop-blur-xl">
              <Sparkles className="h-3.5 w-3.5 fill-emerald-400/20 text-emerald-400" />
              <span className="text-[10px] font-black uppercase tracking-[0.1em] text-white/90">
                Premium Investment Access
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-black leading-[1.1] tracking-tight text-white md:text-6xl">
                Secure Your <br />
                <span className="bg-gradient-to-r from-emerald-400 via-sky-300 to-indigo-300 bg-clip-text text-transparent">
                  Financial Future
                </span>
              </h1>
              <p className="max-w-lg text-base font-medium leading-relaxed text-white/70 md:text-lg">
                Invest in high-growth companies before they hit secondary
                markets. Experience the future of IPO bidding.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/ipo/status">
                <Button
                  size="sm"
                  className="group/btn h-14 rounded-2xl bg-white px-8 text-lg font-black text-secondary transition-all hover:bg-white/95"
                >
                  Check Status
                  <ArrowUpRight className="ml-2 h-5 w-5 transition-transform group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/ipo">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-14 rounded-2xl border-2 border-white/20 bg-white/5 px-8 text-lg font-bold text-white backdrop-blur-lg transition-all hover:bg-white/10"
                >
                  Explore
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Side: 3D Visualization */}
          <div className="relative flex items-center justify-center delay-200 duration-1000 animate-in fade-in slide-in-from-right-12">
            {/* Glowing Orbit */}
            <div className="absolute h-[100%] w-[100%] animate-pulse rounded-full bg-sky-500/5 blur-[80px]" />

            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative flex justify-center lg:justify-end"
              >
                {/* Background Glow */}
                <div className="absolute inset-0 rounded-full bg-primary/20 opacity-30 blur-3xl" />

                {/* Illustration */}
                <div className="relative">
                  <motion.img
                    src="/assets/ipo-hero-product.png"
                    alt="IPO Hero Product"
                    className="w-full max-w-sm rounded-2xl object-contain drop-shadow-2xl md:max-w-md lg:max-w-lg"
                    animate={{
                      y: [0, -12, 0], // Moves up and down
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />

                  {/* FD Interest Card (BOTTOM-RIGHT inside image now) */}
                  <div
                    className="animate-fade-in absolute bottom-4 right-1 hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl lg:block"
                    style={{ animationDelay: "0.2s" }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 shadow-lg shadow-emerald-500/20">
                          <TrendingUp className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <p className="mb-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">
                          Success Rate
                        </p>
                        <p className="text-2xl font-black text-slate-900">
                          98.4
                          <span className="text-lg text-emerald-500">%</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 md:px-6">
        {/* Enhanced Filters */}
        <div className="bg-background/95 sticky top-20 z-30 mb-8 border-b border-border/40 py-4 backdrop-blur-md">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <Tabs
              value={statusFilter}
              onValueChange={setStatusFilter}
              className="w-full sm:w-auto"
            >
              <TabsList className="grid w-full grid-cols-3 bg-muted/50 p-1 sm:flex sm:w-auto">
                <TabsTrigger
                  value="Open"
                  className="gap-2 border border-transparent data-[state=active]:border-0 data-[state=active]:bg-white data-[state=active]:text-[#1dab91]"
                >
                  <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                  Open{" "}
                  <span className="ml-1 rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                    {openIPOs}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="Upcoming"
                  className="gap-2 border border-transparent data-[state=active]:border-0 data-[state=active]:bg-white data-[state=active]:text-[#175d80]"
                >
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  Upcoming{" "}
                  <span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                    {upcomingIPOs}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="Closed"
                  className="border border-transparent data-[state=active]:border-0 data-[state=active]:bg-white"
                >
                  Closed
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2 sm:pb-0">
              {["all", "Main Board", "SME"].map(type => (
                <button
                  key={type}
                  onClick={() => setBoardFilter(type)}
                  className={`whitespace-nowrap rounded-full border px-5 py-2 text-sm font-medium transition-all ${
                    boardFilter === type
                      ? "border-[#175d80] bg-[#175d80] text-white shadow-md"
                      : "border-border bg-white text-muted-foreground hover:border-[#175d80]/50 hover:bg-[#175d80]/5"
                  }`}
                >
                  {type === "all" ? "All Boards" : type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Professional IPO Cards Grid */}
        <div className="mb-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredIPOs.map(ipo => {
            const closeDate = formatDate(ipo.bidDates.end);

            return (
              <Link
                key={ipo.id}
                to={`/ipo/${ipo.id}`}
                className="group block h-full"
              >
                <Card className="h-full overflow-hidden border-border/50 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:border-[#1dab91]/50 hover:shadow-xl group-hover:-translate-y-1">
                  <CardContent className="flex h-full flex-col p-0">
                    {/* Card Header */}
                    <div className="border-b border-border/40 bg-gradient-to-r from-gray-50/50 to-white p-5">
                      <div className="mb-3 flex items-start justify-between gap-4">
                        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm transition-transform group-hover:scale-105">
                          <ProviderIcon
                            iconType={ipo.iconType}
                            size={28}
                            className="text-[#175d80]"
                          />
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {ipo.status === "Open" ? (
                            <Badge className="border-none bg-green-100 px-2.5 py-0.5 text-green-700 shadow-none hover:bg-green-200">
                              <span className="mr-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                              Live
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="border-border/60 text-muted-foreground"
                            >
                              {ipo.status}
                            </Badge>
                          )}
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {ipo.type}
                          </span>
                        </div>
                      </div>

                      <h3
                        className="line-clamp-1 text-lg font-bold leading-tight text-gray-900 transition-colors group-hover:text-[#1dab91]"
                        title={ipo.companyName}
                      >
                        {ipo.companyShortName}
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {ipo.issueSize}
                      </p>
                    </div>

                    {/* Card Body - Stats */}
                    <div className="grid flex-grow grid-cols-2 gap-x-2 gap-y-4 p-5">
                      <div>
                        <p className="mb-0.5 text-xs font-medium text-muted-foreground">
                          Price Range
                        </p>
                        <p className="text-sm font-bold text-gray-800">
                          ₹{ipo.priceRange.min} - ₹{ipo.priceRange.max}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="mb-0.5 text-xs font-medium text-muted-foreground">
                          Min Amount
                        </p>
                        <p className="text-sm font-bold text-gray-800">
                          ₹{ipo.minInvestment.toLocaleString()}
                        </p>
                      </div>

                      <div className="col-span-2 flex items-center justify-between border-t border-dashed border-border/60 pt-3">
                        <div className="flex items-center gap-2">
                          <div className="rounded-lg bg-blue-50 p-1.5 text-blue-600">
                            <Briefcase className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              Lot Size
                            </p>
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-sm font-bold text-gray-700">
                                {ipo.lotSize} Shares
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="mt-auto border-t border-border/40 bg-muted/10 p-4">
                      {ipo.status === "Open" ? (
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-medium uppercase text-muted-foreground">
                              Closes On
                            </span>
                            <span className="text-sm font-bold text-[#175d80]">
                              {closeDate.day} {closeDate.month}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            className="rounded-full bg-[#175d80] px-6 text-white shadow-md transition-all hover:bg-[#134b66] hover:shadow-lg"
                          >
                            Apply Now
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full transition-all hover:border-[#1dab91] hover:bg-white hover:text-[#1dab91] group-hover:shadow-sm"
                        >
                          View Details{" "}
                          <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {filteredIPOs.length === 0 && (
          <Card className="border border-border/50">
            <CardContent className="py-16 text-center">
              <Users className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
              <h3 className="mb-2 text-lg font-semibold text-[#0a344a]">
                No IPOs Found
              </h3>
              <p className="text-muted-foreground">
                No IPOs found for the selected filters. Try changing the filter
                criteria.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Subscription Status Table - Chittorgarh Style */}
        {ipos.filter(ipo => ipo.status === "Open").length > 0 && (
          <Card className="mb-8 border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="h-5 w-5 text-[#1dab91]" />
                  Live Subscription Status (BSE + NSE)
                </CardTitle>
                <Badge variant="outline" className="gap-1">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                  Live Data
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">IPO Name</TableHead>
                      <TableHead className="text-center font-semibold">
                        Close Date
                      </TableHead>
                      <TableHead className="text-center font-semibold">
                        Size (Cr)
                      </TableHead>
                      <TableHead className="text-center font-semibold text-blue-600">
                        QIB
                      </TableHead>
                      <TableHead className="text-center font-semibold text-purple-600">
                        NII
                      </TableHead>
                      <TableHead className="text-center font-semibold text-orange-600">
                        Retail
                      </TableHead>
                      <TableHead className="text-center font-semibold text-[#1dab91]">
                        Total
                      </TableHead>
                      <TableHead className="text-center font-semibold">
                        Apply
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ipos
                      .filter(ipo => ipo.status === "Open")
                      .map(ipo => (
                        <TableRow key={ipo.id} className="hover:bg-muted/30">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                                <TrendingUp className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-secondary">
                                  {ipo.companyShortName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {ipo.type}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {new Date(ipo.bidDates.end).toLocaleDateString(
                              "en-IN",
                              { day: "numeric", month: "short" }
                            )}
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {ipo.issueSize}
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`font-bold ${ipo.subscriptionRate.qib >= 1 ? "text-green-600" : "text-gray-600"}`}
                            >
                              {ipo.subscriptionRate.qib.toFixed(2)}x
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`font-bold ${ipo.subscriptionRate.nii >= 1 ? "text-green-600" : "text-gray-600"}`}
                            >
                              {ipo.subscriptionRate.nii.toFixed(2)}x
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`font-bold ${ipo.subscriptionRate.retail >= 1 ? "text-green-600" : "text-gray-600"}`}
                            >
                              {ipo.subscriptionRate.retail.toFixed(2)}x
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              className={`${ipo.subscriptionRate.total >= 1 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}
                            >
                              {ipo.subscriptionRate.total.toFixed(2)}x
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Link to={`/ipo/${ipo.id}`}>
                              <Button
                                size="sm"
                                className="h-7 bg-[#1dab91] px-3 text-xs hover:bg-[#18937c]"
                              >
                                Apply
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* IPO Timetable */}
        <Card className="mb-8 mt-8 border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-[#175d80]" />
              IPO Timetable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">IPO Name</TableHead>
                    <TableHead className="text-center font-semibold">
                      Open
                    </TableHead>
                    <TableHead className="text-center font-semibold">
                      Close
                    </TableHead>
                    <TableHead className="text-center font-semibold">
                      Allotment
                    </TableHead>
                    <TableHead className="text-center font-semibold">
                      Refund
                    </TableHead>
                    <TableHead className="text-center font-semibold">
                      Listing
                    </TableHead>
                    <TableHead className="text-center font-semibold">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ipos.slice(0, 6).map(ipo => (
                    <TableRow key={ipo.id} className="hover:bg-muted/30">
                      <TableCell>
                        <Link
                          to={`/ipo/${ipo.id}`}
                          className="flex items-center gap-2 hover:text-primary"
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                            <TrendingUp className="h-4 w-4 text-primary" />
                          </div>
                          <span className="font-medium text-secondary hover:text-[#1dab91]">
                            {ipo.companyShortName}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {new Date(ipo.bidDates.start).toLocaleDateString(
                          "en-IN",
                          { day: "numeric", month: "short" }
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {new Date(ipo.bidDates.end).toLocaleDateString(
                          "en-IN",
                          { day: "numeric", month: "short" }
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {new Date(ipo.allotmentDate).toLocaleDateString(
                          "en-IN",
                          { day: "numeric", month: "short" }
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {new Date(ipo.refundDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {new Date(ipo.listingDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          className={`text-xs ${
                            ipo.status === "Open"
                              ? "bg-green-100 text-green-700"
                              : ipo.status === "Upcoming"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {ipo.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Quick Tips */}
        <div className="mb-8 grid gap-4 md:grid-cols-3">
          {[
            {
              icon: Target,
              title: "Apply Early",
              desc: "Submit your application before the last day to avoid technical issues.",
              color: "bg-[#1dab91]/10",
              iconColor: "text-[#1dab91]",
            },
            {
              icon: Star,
              title: "Research Well",
              desc: "Read the RHP and understand company fundamentals before investing.",
              color: "bg-green-50",
              iconColor: "text-green-600",
            },
            {
              icon: ArrowUpRight,
              title: "Diversify",
              desc: "Don't put all your money in one IPO. Spread across multiple offerings.",
              color: "bg-amber-50",
              iconColor: "text-amber-600",
            },
          ].map(tip => (
            <Card
              key={tip.title}
              className="border border-border/50 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 ${tip.color} rounded-xl`}>
                    <tip.icon className={`h-6 w-6 ${tip.iconColor}`} />
                  </div>
                  <div>
                    <h4 className="mb-1 font-bold text-[#0a344a]">
                      {tip.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">{tip.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compliance Disclaimer */}
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-amber-100 p-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="mb-2 font-bold text-[#0a344a]">
                  Important SEBI Disclaimer
                </h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>
                    • IPO investments are subject to market risks. Read all
                    scheme related documents carefully.
                  </li>
                  <li>
                    • Grey Market Premium (GMP) is for informational purposes
                    only and is not endorsed by SEBI.
                  </li>
                  <li>
                    • Do not subscribe based on premium price alone. Consider
                    company fundamentals.
                  </li>
                  <li>
                    • Past performance is not indicative of future results.
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ProductLayout>
  );
};

export default IPOHomePage;
